import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { SettingsEditState } from '@ops/services';
import {
  LocalStorageService,
  OpsSubscriptionModalService,
  SessionManagerService,
  SubscriptionStatus,
  User,
  UsersService,
} from '@ops/shared-types';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-team-member',
  templateUrl: './ops-team-member.component.html',
  styleUrl: './ops-team-member.component.scss',
})
export class OpsTeamMemberComponent implements OnInit, OnDestroy {
  userRole = 'guest';

  editState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getEditState
  );

  saveState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getSaveState
  );

  isPreview = true;

  subs: Array<Subscription> = [];

  errorsTrack = {
    oldPassword: "Current password can't be empty",
    newPassword: "New password can't be empty",
    confirmedPassword: "Confirm password can't be empty",
  };

  constructor(
    private localStorageService: LocalStorageService,
    private sessionManager: SessionManagerService,
    private usersService: UsersService,
    private modalService: OpsSubscriptionModalService,
    private translateService: TranslateService
  ) {}

  members = [];

  urSub: Subscription;

  confirmSubscription: Subscription;

  ngOnInit(): void {
    const eState = this.editState$.subscribe((isEditing: any) => {
      this.isPreview = !isEditing;

      if (!isEditing) {
        this.addingNewMember = false;
      }

      this.errorsTrack = {
        oldPassword: "Current password can't be empty",
        newPassword: "New password can't be empty",
        confirmedPassword: "Confirm password can't be empty",
      };
    });

    const sState = this.saveState$.subscribe((saveChanges: any) => {
      if (saveChanges === true) {
        // console.log('SAVE CHANGES');

        const errorFields = Object.keys(this.errorsTrack);
        // if (errorFields.length === 0 && this.passwordMissmatch === false) {
        //   this.saveNewPassword();
        //   this.store.dispatch(new UnSavedSettingsEdit());
        // } else {
        //   const errorsList = errorFields.map((errorKey) => {
        //     return '\n' + errorKey + ': ' + this.errorsTrack[errorKey];
        //   });

        //   const missmatch = this.passwordMissmatch
        //     ? '\nNew and Confirm Password missmatch'
        //     : '';

        //   const joinMessage = errorsList.join('') + missmatch;

        //   this.store.dispatch(new UnSavedSettingsEdit());

        //   window.alert(
        //     'Some fields are invalid...\nPlease check fields:\n' + joinMessage
        //   );
        // }
      }
    });
    const lsCompany = this.localStorageService.getCurrentCompany();
    this.usersService
      .getUsersForCompany(lsCompany._id)
      .subscribe((users: User[]) => {
        const filterUsersForCompany = users?.filter((userItem) => {
          const companyMatch = userItem?.companies?.filter(
            (cItem) => cItem?.company === lsCompany._id
          );
          return companyMatch.length > 0;
        });

        const usersMap = filterUsersForCompany?.map((userItem) => {
          const companyRole = userItem?.companies?.find(
            (cItem) => cItem?.company === lsCompany._id
          ).role;

          const userName = [];

          if (userItem.firstName) {
            userName.push(userItem.firstName);
          }

          if (userItem.lastName) {
            userName.push(userItem.lastName);
          }

          if (userName.length === 0) {
            userName.push('Not Available');
            userName.push(`(${userItem['username']})`);
          }

          return {
            _id: userItem._id,
            name: userName.join(' '),
            email: userItem.email,
            isVerified: userItem['isVerified'],
            isAdmin: userItem.isAdmin,
            role: companyRole,
          };
        });

        this.members = usersMap;
      });

    this.urSub = this.sessionManager
      .getUserRoleObservable()
      .subscribe((userRole: string) => {
        this.userRole = userRole;
      });

    this.subs.push(eState);
    this.subs.push(sState);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());

    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }

    if (this.urSub?.unsubscribe) {
      this.urSub.unsubscribe();
    }

    this.userRole = 'guest';
  }

  get userIsAdmin(): boolean {
    return this.userRole === 'admin';
  }

  addingNewMember = false;

  roleOptions = ['admin', 'staff', 'guest'];

  invitedMember(createdUser: User) {
    const lsCompany = this.localStorageService.getCurrentCompany();
    const companyRole = createdUser.companies.find(
      (cItem) => cItem.company === lsCompany._id
    ).role;

    const userName = [];

    if (createdUser.firstName) {
      userName.push(createdUser.firstName);
    }

    if (createdUser.lastName) {
      userName.push(createdUser.lastName);
    }

    if (userName.length === 0) {
      userName.push('Not Available');
      userName.push(`(${createdUser['username']})`);
    }

    const mappedUser = {
      _id: createdUser._id,
      name: userName.join(' '),
      email: createdUser.email,
      isVerified: createdUser['isVerified'],
      isAdmin: createdUser.isAdmin,
      role: companyRole,
    };

    this.members.push(mappedUser);
  }

  confirmedDeleteTeamMember(teamMember: User) {
    const lsCompany = this.localStorageService.getCurrentCompany();
    this.usersService
      .deleteUserFromCompany(teamMember._id, lsCompany._id)
      .subscribe((resp) => {
        this.members = this.members.filter(
          (userItem) => userItem._id !== teamMember._id
        );
      });
  }

  deleteTeamMember(teamMember: User) {
    const titleText = this.translateService.instant('windowAlert.opsTeamMember.confirmRemoval');
    const confirmationText = this.translateService.instant('windowAlert.opsTeamMember.removeUserAccessConfirmation', { email: teamMember.email });
    this.showConfirmationModal(
      titleText,
      confirmationText,
      this.confirmedDeleteTeamMember.bind(this, teamMember)
    );
  }

  onRoleChange(value, memberItem) {
    memberItem.role = value;
    const lsCompany = this.localStorageService.getCurrentCompany();
    this.usersService
      .addOrUpdateUserCompanyRole(memberItem._id, {
        company: lsCompany._id,
        role: value,
      })
      .subscribe(
        (userUpdated: string) => {
          // window.alert(`Invitation sent to ${this.memberEmail}.`);
          // if (userInvited instanceof String) {
          // this.invitedMember.emit(JSON.parse(userInvited) as unknown as User);
          // }
          // console.log('userUpdated', userUpdated);
        },
        (error) => {
          //    console.log('Error', error);
          if (error?.error?.message) {
            const errorsList = error?.error?.message.split('.');
            const errorDescription =
              'Update Error\n\nSome fields are invalid...\nPlease check fields:\n\n';

            const fullMessage = this.translateService.instant('windowAlert.opsTeamMember.updateError', { errors: errorsList.join('.\n') });
            window.alert(fullMessage);
          }
        }
      );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active':
        return 'status-active';
      case 'Invitation Pending':
        return 'status-invitation-pending';
      case 'Registration Pending':
        return 'status-registration-pending';
      case 'Cancel':
        return 'status-cancel';
      default:
        return '';
    }
  }

  getVerifiedClass(isVerified: boolean): string {
    if (isVerified) {
      return 'status-active';
    }

    return 'status-invitation-pending';
  }

  addNewMember() {
    this.addingNewMember = true;
  }

  cancelAddNewMember() {
    this.addingNewMember = false;
  }

  // validateMatch() {
  //   if (
  //     this.newPassword &&
  //     this.confirmedPassword &&
  //     this.newPassword.length >= 6 &&
  //     this.confirmedPassword.length >= 6
  //   ) {
  //     if (this.newPassword !== this.confirmedPassword) {
  //       this.passwordMissmatch = true;
  //     } else {
  //       this.passwordMissmatch = false;
  //     }
  //   } else {
  //     this.passwordMissmatch = false;
  //   }
  // }

  // onInputChange(newValue: string, field?: string): void {
  //   this[field] = newValue;

  //   this.validateMatch();
  // }

  showConfirmationModal(
    titleText: string,
    bodyText: string,
    confirmationCallBack: () => void,
    cancelationCallBack?: () => void
  ) {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.confirmSubscription = this.modalService
      .openModal({
        statusType: SubscriptionStatus.CONFIRMATION,
        modalTitle: titleText,
        modalText: bodyText,
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          // User confirmed
          // console.log('User confirmed cancellation');
          confirmationCallBack();
        } else {
          // User declined
          // console.log('User cancelled the action');
          cancelationCallBack && cancelationCallBack();
        }
      });
  }
}
